import { useCallback, useState } from "react";
import CreateAccount from "../CreateAccount/CreateAccount/CreateAccount.web";
import Login from "../Login/Login.web";
import PasswordReset from "../PasswordReset/PasswordReset/PasswordReset.web";

enum SCREENS {
  LOGIN = "login",
  SIGNUP = "signup",
  FORGOT_PASSWORD = "forgot_password",
}
export { SCREENS as AuthenticationContainerScreen };

type AuthenticationContainerProps = {
  onSuccess: (data: { screen: SCREENS }) => void;
  onScreenChange?: (data: { screen: SCREENS }) => void;
  startingScreen?: SCREENS;
  /**
   * In extreme cases, this allows overriding the Login element.
   */
  customLoginScreen?: (props: {
    onForgotPasswordClick: () => void;
    onSignupClick: () => void;
    onSuccess: () => void;
  }) => JSX.Element;
};

/**
 * Container for encapsulating our current common authentication flows: Login and Signup.
 * This is meant to make it easier to drop our authentication flows in the app without having to duplicate logic for
 * screen routing and/or analytics.
 *
 * Does not involve itself in supplementary flows like Claiming Accounts (another auth flow, though not meant
 * to be reusable in other forms.), but it does include the Forgot Password flow; despite that only being
 * applicable to Login.
 * This is because we expose callback functions for going-to and coming-from this flow
 * (eg. Login -> ForgotPassword,  ForgotPassword -> Login) to be able to override it in some areas. So
 * to make it easier, this is still kept as another screen in this component.
 */
export function AuthenticationContainer({
  onSuccess,
  onScreenChange,
  startingScreen = SCREENS.LOGIN,
  customLoginScreen,
}: AuthenticationContainerProps) {
  const [screen, setScreen] = useState<SCREENS>(startingScreen);

  const handleScreenChange = useCallback(
    (screen: SCREENS) => {
      setScreen(screen);
      onScreenChange?.({ screen });
    },
    [onScreenChange]
  );

  const LoginScreen = customLoginScreen ?? Login;

  return (
    <>
      {screen === SCREENS.LOGIN && (
        <LoginScreen
          onSuccess={() => onSuccess({ screen: SCREENS.LOGIN })}
          onSignupClick={() => handleScreenChange(SCREENS.SIGNUP)}
          onForgotPasswordClick={() =>
            handleScreenChange(SCREENS.FORGOT_PASSWORD)
          }
        />
      )}
      {screen === SCREENS.SIGNUP && (
        <CreateAccount
          onSuccess={() => onSuccess({ screen: SCREENS.SIGNUP })}
          onLoginClick={() => handleScreenChange(SCREENS.LOGIN)}
        />
      )}
      {screen === SCREENS.FORGOT_PASSWORD && (
        <PasswordReset onLoginClick={() => handleScreenChange(SCREENS.LOGIN)} />
      )}
    </>
  );
}
